import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiFileText, FiGift, FiArrowRight, FiClock } from "react-icons/fi";
import {
  resumenBoletas,
  tieneGratificacion,
  ultimoPeriodoDisponible,
} from "../../services/boletasService";
import { fmtPeriodo } from "./boletasFormat";

const pad = (n) => String(n).padStart(2, "0");

// "2026-09" -> "01/10/2026": las boletas de un mes se publican el día 1 del siguiente.
function fechaDisponible(periodo) {
  const [y, m] = periodo.split("-").map(Number);
  return m === 12 ? `01/01/${y + 1}` : `01/${pad(m + 1)}/${y}`;
}

// Qué periodo muestra la tarjeta según el filtro del dashboard:
//  - Mes concreto: ese mes; si aún no está disponible -> { pendiente: true }.
//  - "Todos" (0): diciembre del año, o el último periodo disponible si aún no llega.
function periodoObjetivo(month, year) {
  const max = ultimoPeriodoDisponible();
  if (month) {
    const periodo = `${year}-${pad(month)}`;
    return { periodo, pendiente: periodo > max, ajustado: false };
  }
  const diciembre = `${year}-12`;
  return diciembre > max
    ? { periodo: max, pendiente: false, ajustado: true }
    : { periodo: diciembre, pendiente: false, ajustado: false };
}

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Porcentaje entero, acotado a 0–100 (sin total conocido -> null).
const pct = ({ firmadas, total }) => (total ? Math.min(100, Math.round((firmadas / total) * 100)) : null);

// "1 de 59 firmadas" o, si el backend no da el total, "1 firmada".
const textoConteo = (r) =>
  r.total !== null ? `${r.firmadas} de ${r.total} firmadas` : plural(r.firmadas, "firmada", "firmadas");

// El backend informa TOTAL = 0 cuando la planilla del periodo aún no se carga.
const sinPlanilla = (r) => r?.total === 0;

function AvisoPendiente({ children }) {
  return (
    <div className="dash-boletas dash-boletas--pendiente" role="status">
      <FiClock className="dash-boletas__clock" aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

/**
 * Resumen de boletas firmadas del mes filtrado en el dashboard.
 * En julio y diciembre muestra además la gratificación ("AAAA-MMG");
 * si esa consulta falla no afecta al total de la remuneración.
 * Un mes que aún no tiene boletas muestra solo un aviso, sin consultar.
 */
export default function BoletasResumenCard({ month, year }) {
  const { periodo, pendiente, ajustado } = periodoObjetivo(month, year);
  const conGrati = !pendiente && tieneGratificacion(periodo);

  // rem / grati: { firmadas, total: number | null }; grati también null (mes sin
  // gratificación) o "error" (falló solo esa consulta). `periodo` indica a qué
  // periodo corresponden los datos, para no mostrar los de un filtro anterior.
  const [datos, setEstado] = useState({ periodo: "", loading: true, error: "", rem: null, grati: null });
  const estado = datos.periodo === periodo ? datos : { ...datos, loading: true, rem: null, grati: null };

  useEffect(() => {
    if (pendiente) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    setEstado({ periodo, loading: true, error: "", rem: null, grati: null });

    const pedidos = [resumenBoletas(periodo, { signal })];
    if (conGrati) pedidos.push(resumenBoletas(`${periodo}G`, { signal }));

    Promise.allSettled(pedidos).then(([rem, grati]) => {
      if (signal.aborted) return;
      setEstado({
        periodo,
        loading: false,
        error: rem.status === "rejected" ? rem.reason?.message || "No se pudo consultar." : "",
        rem: rem.status === "fulfilled" ? rem.value : null,
        grati: !grati ? null : grati.status === "fulfilled" ? grati.value : "error",
      });
    });

    return () => controller.abort();
  }, [periodo, conGrati, pendiente]);

  const nombre = fmtPeriodo(periodo).toLowerCase();

  if (pendiente) {
    return (
      <AvisoPendiente>
        Las boletas de {nombre} estarán disponibles desde el {fechaDisponible(periodo)}.
      </AvisoPendiente>
    );
  }

  const rem = estado.rem;
  if (!estado.loading && sinPlanilla(rem)) {
    return <AvisoPendiente>Las boletas de {nombre} aún no se han cargado.</AvisoPendiente>;
  }

  const remPct = rem ? pct(rem) : null;
  // La gratificación sin planilla cargada no se muestra.
  const mostrarGrati = conGrati && !sinPlanilla(estado.grati);

  const nota = ajustado
    ? "Último periodo disponible."
    : month
    ? "Confirmadas por el personal."
    : "Último periodo del año seleccionado.";

  return (
    <div className="dash-boletas" aria-busy={estado.loading} aria-live="polite">
      <div className="dash-boletas__main">
        <span className="dash-boletas__icon" aria-hidden="true">
          <FiFileText />
        </span>
        <div>
          <span className="dash-kpi-label">Boletas firmadas · {fmtPeriodo(periodo)}</span>
          {estado.loading ? (
            <strong className="dash-kpi-value dash-boletas__loading">…</strong>
          ) : estado.error ? (
            <span className="dash-boletas__error">{estado.error}</span>
          ) : (
            <strong className="dash-kpi-value">
              {rem.firmadas}
              {rem.total !== null && <span className="dash-boletas__total"> / {rem.total}</span>}
            </strong>
          )}
          {remPct !== null && (
            <div className="dash-boletas__progress">
              <progress
                value={Math.min(rem.firmadas, rem.total)}
                max={rem.total}
                aria-label={`${rem.firmadas} de ${rem.total} boletas firmadas`}
              />
              <span>
                {remPct}% · {plural(Math.max(0, rem.total - rem.firmadas), "pendiente", "pendientes")}
              </span>
            </div>
          )}
          <span className="dash-kpi-note">{nota}</span>
        </div>
      </div>

      {mostrarGrati && (
        <div className="dash-boletas__grati">
          <FiGift aria-hidden="true" />
          <span>
            Gratificación:{" "}
            <strong>
              {estado.loading
                ? "…"
                : estado.grati === "error"
                ? "no disponible"
                : textoConteo(estado.grati)}
            </strong>
          </span>
          {!estado.loading && estado.grati !== "error" && (
            <Link className="dash-boletas__link dash-boletas__link--sm" to={`/boletas-firmadas?periodo=${periodo}G`}>
              Ver
            </Link>
          )}
        </div>
      )}

      <Link className="dash-boletas__link" to={`/boletas-firmadas?periodo=${periodo}`}>
        Ver boletas <FiArrowRight aria-hidden="true" />
      </Link>
    </div>
  );
}
